import { Controller } from "@hotwired/stimulus";
const POSTS_REMAINING_BEFORE_LOAD = 4;
const POST_RENDER_BATCH_SIZE = 5;
const PREFETCH_DISTANCE = 2000;

export default class CursorPaginationController extends Controller {
  static targets = ["loader", "scrollContainer"];

  connect() {
    this.loading = false;
    this.loadedUrls = new Set();
    this.retryCount = new Map();
    this.retryTimer = null;
    this.abortController = null;
    this.observeFrame = null;
    this.anchorFrame = null;

    this.handleIntersection = this.handleIntersection.bind(this);
    this.captureScrollAnchor = this.captureScrollAnchor.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);

    // Bootstrap XXL breakpoint used by overflow-xxl-auto.
    this.breakpoint = globalThis.matchMedia("(min-width: 1400px)");
    this.breakpoint.addEventListener(
      "change",
      this.handleBreakpointChange
    );

    this.createObserver();
    this.watchPostList();
    this.scrollRoot.addEventListener(
      "scrollend",
      this.captureScrollAnchor
    );
    this.schedulePaginationTrigger();
    this.scheduleAnchorCapture();
  }

  disconnect() {
    this.observer?.disconnect();
    this.postListObserver?.disconnect();
    this.abortController?.abort();

    if (this.observeFrame) {
      cancelAnimationFrame(this.observeFrame);
    }

    if (this.anchorFrame) {
      cancelAnimationFrame(this.anchorFrame);
    }

    this.scrollRoot.removeEventListener(
      "scrollend",
      this.captureScrollAnchor
    );
    this.breakpoint?.removeEventListener(
      "change",
      this.handleBreakpointChange
    );

    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }

  loaderTargetConnected() {
    this.schedulePaginationTrigger();
  }

  watchPostList() {
    const postList = this.element.querySelector("#post_list");
    if (!postList) return;

    this.postListObserver = new MutationObserver(() => {
      this.schedulePaginationTrigger();
      this.scheduleAnchorCapture();
    });
    this.postListObserver.observe(postList, { childList: true });
  }

  schedulePaginationTrigger() {
    if (this.loading) return;

    if (this.observeFrame) {
      cancelAnimationFrame(this.observeFrame);
    }

    // Turbo applies the appended posts and the new cursor in separate stream
    // actions. Wait two frames so the trigger always uses the completed batch.
    this.observeFrame = requestAnimationFrame(() => {
      this.observeFrame = requestAnimationFrame(() => {
        this.observeFrame = null;
        this.observePaginationTrigger();
      });
    });
  }

  get scrollRoot() {
    return this.hasScrollContainerTarget
      ? this.scrollContainerTarget
      : this.element;
  }

  createObserver() {
    this.observer?.disconnect();

    this.observer = new IntersectionObserver(
      this.handleIntersection,
      {
        root: this.scrollRoot,
        rootMargin: `0px 0px ${PREFETCH_DISTANCE}px 0px`,
        threshold: 0.01
      }
    );
  }

  scheduleAnchorCapture() {
    if (this.anchorFrame) cancelAnimationFrame(this.anchorFrame);

    this.anchorFrame = requestAnimationFrame(() => {
      this.anchorFrame = null;
      this.captureScrollAnchor();
    });
  }

  captureScrollAnchor() {
    const postList = this.element.querySelector("#post_list");
    if (!postList) return;

    const rootTop = this.scrollRoot.getBoundingClientRect().top;
    const posts = postList.querySelectorAll(
      ":scope > turbo-frame[id^=\"repost_list_\"]"
    );

    const anchor = Array.from(posts).find(post => {
      return post.getBoundingClientRect().bottom > rootTop + 1;
    });

    if (!anchor) return;

    this.scrollAnchor = anchor;
    this.scrollAnchorOffset =
      anchor.getBoundingClientRect().top - rootTop;
  }

  handleBreakpointChange() {
    if (!this.scrollAnchor?.isConnected) {
      this.scheduleAnchorCapture();
      return;
    }

    requestAnimationFrame(() => {
      if (!this.scrollAnchor?.isConnected) return;

      const rootTop = this.scrollRoot.getBoundingClientRect().top;
      const currentOffset =
        this.scrollAnchor.getBoundingClientRect().top - rootTop;

      this.scrollRoot.scrollTop +=
        currentOffset - this.scrollAnchorOffset;
    });
  }

  observePaginationTrigger() {
    if (!this.observer || !this.hasLoaderTarget) return;

    this.observer.disconnect();
    const trigger = this.paginationTrigger;
    this.observer.observe(trigger);

    // Keep the loader as a fallback for short feeds and fast scroll jumps.
    if (trigger !== this.loaderTarget) {
      this.observer.observe(this.loaderTarget);
    }

    // On hard refresh or browser history restoration the trigger can already
    // be visible before IntersectionObserver sends its initial notification.
    // Check once immediately so pagination cannot remain idle in that state.
    if (this.isInsideScrollViewport(trigger)) {
      this.loadNext();
    }
  }

  isInsideScrollViewport(element) {
    const rootRect = this.scrollRoot.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    return elementRect.bottom > rootRect.top &&
      elementRect.top < rootRect.bottom + PREFETCH_DISTANCE;
  }

  get paginationTrigger() {
    const postList = this.element.querySelector("#post_list");
    if (!postList) return this.loaderTarget;

    const posts = Array.from(postList.children).filter(element => {
      return element.matches("turbo-frame.repost_list_frame");
    });

    if (posts.length <= POSTS_REMAINING_BEFORE_LOAD) {
      return this.loaderTarget;
    }

    // Observe the first post in the final group of four. The previous `- 1`
    // selected a fifth-from-last trigger instead.
    return posts[posts.length - POSTS_REMAINING_BEFORE_LOAD];
  }

  handleIntersection(entries) {
    const triggerIsVisible = entries.some(entry => {
      return entry.isIntersecting;
    });

    if (triggerIsVisible) {
      this.loadNext();
    }
  }

  async loadNext() {
    if (this.loading || !this.hasLoaderTarget) return;

    const loader = this.loaderTarget;
    const url = loader.dataset.url;

    if (!url || this.loadedUrls.has(url)) return;

    this.loading = true;
    this.loadedUrls.add(url);
    this.observer?.disconnect();

    this.abortController = new AbortController();
    let succeeded = false;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        },
        cache: "no-store",
        credentials: "same-origin",
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(
          `Pagination failed with status ${response.status}`
        );
      }

      const html = await response.text();

      if (!html.trim()) {
        throw new Error("Pagination returned an empty response");
      }

      await this.renderStreamMessage(html);

      this.retryCount.delete(url);
      succeeded = true;
    } catch (error) {
      if (error.name === "AbortError") return;

      console.error("Cursor pagination error:", error);

      this.loadedUrls.delete(url);
      this.scheduleRetry(url);
    } finally {
      this.loading = false;
      this.abortController = null;

      if (succeeded) {
        this.schedulePaginationTrigger();
      }
    }
  }

  async renderStreamMessage(html) {
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const appendStream = documentFragment.querySelector(
      "turbo-stream[action=\"append\"][target=\"post_list\"]"
    );
    const appendTemplate = appendStream?.querySelector("template");
    const postFrames = appendTemplate
      ? Array.from(appendTemplate.content.children)
      : [];

    if (postFrames.length <= POST_RENDER_BATCH_SIZE) {
      Turbo.renderStreamMessage(html);
      return;
    }

    appendStream.remove();

    for (let index = 0; index < postFrames.length; index += POST_RENDER_BATCH_SIZE) {
      const batch = postFrames.slice(index, index + POST_RENDER_BATCH_SIZE);
      const batchStream = document.createElement("turbo-stream");
      const template = document.createElement("template");

      for (const attribute of appendStream.attributes) {
        batchStream.setAttribute(attribute.name, attribute.value);
      }

      template.innerHTML = batch.map(post => post.outerHTML).join("");
      batchStream.append(template);
      Turbo.renderStreamMessage(batchStream.outerHTML);

      await new Promise(resolve => {
        requestAnimationFrame(() => setTimeout(resolve, 0));
      });
    }

    const remainingStreams = Array.from(
      documentFragment.querySelectorAll("turbo-stream")
    ).map(stream => stream.outerHTML).join("");

    if (remainingStreams) Turbo.renderStreamMessage(remainingStreams);
  }

  scheduleRetry(url) {
    const attempts = (this.retryCount.get(url) || 0) + 1;

    this.retryCount.set(url, attempts);

    // Stop automatic retries after three failures.
    if (attempts > 3) return;

    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }

    const retryDelay = attempts * 2000;

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.schedulePaginationTrigger();
    }, retryDelay);
  }
}
